import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import { createCheckoutSession } from '../services/stripeBillingService';
import './Dashboard.css';

// Per-symbol SKU catalog per the 2026-05-19 (revised) bundling model.
// Each row is one Stripe subscription. The customer picks any combination;
// volume discount kicks in automatically at 2+ non-Premier SKUs (handled
// server-side by stripeCheckoutSession + stripeWebhook).
const PRODUCTS = [
  {
    key: 'premier',
    section: 'Premier',
    subcategory: '',
    name: 'Premier',
    monthlyPrice: 29.99,
    yearlyPrice: 299.99,
    color: '#3b82f6',
    checkColor: '#4ade80',
    features: [
      'Equity (stock) signals',
      'Equity options signals',
      'SMS alerting on reports',
      'Daily + hourly IV reports (free baseline)',
      'Custom watchlists',
      'Stock scanner',
    ],
  },
  {
    key: 'spx',
    section: 'Indexes',
    subcategory: 'Tastytrade Signals',
    name: 'SPX',
    monthlyPrice: 79.99,
    yearlyPrice: 799.99,
    color: '#8b5cf6',
    checkColor: '#a78bfa',
    features: [
      'SPX 0DTE credit spread signals',
      'Multi-day SPX options strategies',
      'AI confidence scoring & ranking',
      'Real-time skew & IV analysis',
      'Word report downloads',
    ],
  },
  {
    key: 'spy',
    section: 'Indexes',
    subcategory: 'Tastytrade Signals',
    name: 'SPY',
    monthlyPrice: 49.99,
    yearlyPrice: 499.99,
    color: '#06b6d4',
    checkColor: '#22d3ee',
    features: [
      'SPY put-sell credit spread signals',
      'Options chain analysis',
      'AI-ranked trade candidates',
      'IV & skew monitoring',
    ],
  },
  {
    key: 'es',
    section: 'Futures Options',
    subcategory: 'Tastytrade Signals',
    name: 'ES',
    monthlyPrice: 79.99,
    yearlyPrice: 799.99,
    color: '#f59e0b',
    checkColor: '#fbbf24',
    features: [
      'E-mini S&P 500 futures options signals',
      'Futures-specific IV analysis',
      'TastyTrade chain integration',
      'AI confidence scoring',
      'Quarterly series support',
    ],
  },
  {
    key: 'mes',
    section: 'Futures Options',
    subcategory: 'Tastytrade Signals',
    name: 'MES',
    monthlyPrice: 49.99,
    yearlyPrice: 499.99,
    color: '#10b981',
    checkColor: '#34d399',
    features: [
      'Micro E-mini S&P 500 signals',
      'Lower capital requirement',
      'Same AI analysis as ES',
      'Perfect for smaller accounts',
    ],
  },
];

// Section render order
const SECTIONS = ['Premier', 'Indexes', 'Futures Options'];

declare global {
  interface Window { paypal?: any; }
}

export const Upgrade: React.FC = () => {
  const { user, token } = useAuth();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [pendingProduct, setPendingProduct] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const isPremier = user?.effectiveTier === 'premier' || user?.effectiveTier === 'pro';

  // A SKU is "active" when the customer already has it. Checks per-product
  // entitlement keys (new Stripe purchases) AND legacy per-product flags
  // (grandfathered customers via PayPal / pre-entitlement migration).
  const entitlements: string[] = ((user as any)?.entitlements as string[]) || [];
  const has = (key: string) => entitlements.includes(key);

  const isProductActive = (key: string): boolean => {
    if (key === 'premier') return isPremier || false;
    if (key === 'spx') return !!user?.spxSignalsEnabled || has('notches.signals.options.spx');
    if (key === 'spy') return !!(user as any)?.spySignalsEnabled || !!(user as any)?.indexSignalsEnabled || has('notches.signals.options.spy');
    if (key === 'es') return !!user?.esSignalsEnabled || !!(user as any)?.indexSignalsEnabled || has('notches.signals.options.es');
    if (key === 'mes') return !!user?.mesSignalsEnabled || !!(user as any)?.indexSignalsEnabled || has('notches.signals.options.mes');
    if (key === 'learnToTrade') return (user as any)?.tradeWithMeEnabled || false;
    return false;
  };

  // Count active non-Premier SKUs for volume-discount preview banner. The
  // count includes future analyses (Market/Industry/Investment) once shipped.
  const NON_PREMIER_SKUS = ['spx', 'spy', 'es', 'mes'];
  const nonPremierActiveCount = NON_PREMIER_SKUS.filter((k) => isProductActive(k)).length;
  const nextDiscount =
    nonPremierActiveCount === 0 ? { next: '10% off', at: 2 } :
    nonPremierActiveCount === 1 ? { next: '10% off', at: 2 } :
    nonPremierActiveCount === 2 ? { next: '20% off', at: 3 } :
    null;

  // Stripe Checkout — POST to create-stripe-session, redirect to returned URL.
  // The webhook (stripe-webhook Lambda) handles activation server-side once
  // payment succeeds; the user lands on /subscription/success after Stripe.
  const handleStripeSubscribe = async (product: any) => {
    if (!token) return;
    setStripeError(null);
    setPendingProduct(product.key);
    try {
      const billing: 'monthly' | 'yearly' =
        // Bundle and Algo are monthly-only on Stripe
        ['bundle', 'algo'].includes(product.key) ? 'monthly' : billingCycle;
      const { url } = await createCheckoutSession(token, product.key, billing);
      window.location.assign(url);
    } catch (err: any) {
      console.error('Stripe checkout error:', err);
      setStripeError(
        err?.message ||
        'There was an error starting checkout. Please try again or contact support.'
      );
      setPendingProduct(null);
    }
  };

  const faqs = [
    {
      q: 'How does billing work?',
      a: 'Billed today for the first month (or year if you choose annual), then automatically every billing cycle until you cancel. There is no free trial — paying upfront prevents free-rider activity. You can cancel anytime from your Profile page; you keep access through the end of the current billing period and you are not charged again.'
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes. Cancel from your Profile page at any time and you keep access through the end of the period you have already paid for. There are no cancellation fees and no further charges after that. Refunds for partial periods are not provided.'
    },
    {
      q: 'How does the bundling and volume discount work?',
      a: 'Pick any combination of services. Premier is always full price. Non-Premier services (Indexes, Futures Options, and future analyses) get a volume discount based on how many you subscribe to: 1 service = full price, 2 services = 10% off the non-Premier total, 3+ services = 20% off. The discount applies automatically at checkout and on each renewal.'
    },
    {
      q: 'What does each service cover?',
      a: 'Premier ($29.99/mo) covers stock and equity-options signals plus SMS alerts. SPX ($79.99/mo) and SPY ($49.99/mo) cover index options. ES ($79.99/mo) and MES ($49.99/mo) cover futures options. Each is independent - subscribe to any combination based on what you trade.'
    },
    {
      q: 'What\'s the difference between your signals and ChatGPT?',
      a: 'Our signals are generated by a quantitative engine analyzing live option chains, real-time IV/skew data, and market microstructure — not a language model guessing. ChatGPT has no access to live market data, cannot evaluate real bid/ask spreads, and cannot manage risk. Our system runs on TastyTrade market data with backtested models.'
    },
    {
      q: 'What is the Algo Trading Engine?',
      a: 'The Algo Engine automates stock trading with AI-driven signals and Alpaca brokerage execution. It includes risk management (trailing stops, drawdown protection, profit protection). Requires an active Premier subscription.'
    },
    {
      q: 'How do I update my payment method?',
      a: 'From your Profile page, click "Manage Subscription" — that opens a self-service portal where you can update your card, change billing address, view invoices, and cancel.'
    }
  ];

  const cardStyle: React.CSSProperties = {
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '0.85rem',
    backgroundColor: 'rgba(255,255,255,0.05)',
    position: 'relative',
    backdropFilter: 'blur(10px)',
    transition: 'transform 0.2s',
    display: 'flex',
    flexDirection: 'column',
    width: '240px',
    flex: '0 0 240px',
    boxSizing: 'border-box',
  };


  return (
    <div className="dashboard-container">
      <Navigation />

      <div className="dashboard-content" style={{ maxWidth: '1400px', margin: '2rem auto', padding: '0 1rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2.2rem', marginBottom: '0.75rem', color: '#ffffff' }}>Signal Products</h2>
          <p style={{ fontSize: '1rem', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
            Professional-grade quantitative signals powered by live market data — not a chatbot guessing.
          </p>
          {user?.hasComplimentaryAccess && (
            <div style={{
              marginTop: '1rem', padding: '0.75rem',
              backgroundColor: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: '8px', color: '#4ade80', display: 'inline-block', fontSize: '0.9rem'
            }}>
              Complimentary <strong style={{ textTransform: 'capitalize' }}>{user.effectiveTier}</strong> access active
            </div>
          )}
        </div>

        {/* Billing Cycle Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <span style={{ color: billingCycle === 'monthly' ? '#ffffff' : '#64748b', fontWeight: billingCycle === 'monthly' ? 700 : 400, fontSize: '0.95rem', cursor: 'pointer' }}
            onClick={() => setBillingCycle('monthly')}>Monthly</span>
          <div
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            style={{
              width: 52, height: 28, borderRadius: 14, cursor: 'pointer', position: 'relative',
              backgroundColor: billingCycle === 'yearly' ? '#8b5cf6' : '#374151',
              transition: 'background-color 0.2s',
            }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', backgroundColor: '#ffffff',
              position: 'absolute', top: 3,
              left: billingCycle === 'yearly' ? 27 : 3,
              transition: 'left 0.2s',
            }} />
          </div>
          <span style={{ color: billingCycle === 'yearly' ? '#ffffff' : '#64748b', fontWeight: billingCycle === 'yearly' ? 700 : 400, fontSize: '0.95rem', cursor: 'pointer' }}
            onClick={() => setBillingCycle('yearly')}>
            Yearly
          </span>
          {billingCycle === 'yearly' && (
            <span style={{ backgroundColor: '#16a34a', color: 'white', padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700 }}>
              SAVE 17%
            </span>
          )}
        </div>

        {/* Stripe error banner */}
        {stripeError && (
          <div style={{
            padding: '0.85rem 1rem',
            marginBottom: '1.25rem',
            borderRadius: '8px',
            backgroundColor: 'rgba(248,113,113,0.1)',
            border: '1px solid rgba(248,113,113,0.3)',
            color: '#fecaca',
            fontSize: '0.9rem',
          }}>
            <strong>Checkout error:</strong> {stripeError}
            <button
              onClick={() => setStripeError(null)}
              style={{
                float: 'right',
                background: 'none', border: 'none',
                color: '#fecaca', cursor: 'pointer',
                fontSize: '1.1rem', padding: '0 0.25rem',
              }}
              aria-label="Dismiss"
            >&times;</button>
          </div>
        )}

        {/* Volume discount info — show only if Indexes / Futures Options are in scope */}
        <div style={{
          padding: '1rem 1.25rem', marginBottom: '1.5rem',
          backgroundColor: 'rgba(139,92,246,0.08)', borderRadius: '10px',
          border: '1px solid rgba(139,92,246,0.2)',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem'
        }}>
          <div style={{ flex: '1 1 auto', minWidth: 280 }}>
            <div style={{ color: '#c4b5fd', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Volume discount on non-Premier services
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              1 service = full price &middot; 2 services = <strong style={{ color: '#a78bfa' }}>10% off</strong> &middot; 3+ services = <strong style={{ color: '#a78bfa' }}>20% off</strong>. Premier always at $29.
            </div>
          </div>
          {nextDiscount && nonPremierActiveCount > 0 && (
            <div style={{
              padding: '0.5rem 0.85rem', borderRadius: '8px',
              backgroundColor: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)',
              color: '#4ade80', fontSize: '0.85rem', fontWeight: 600,
            }}>
              Add 1 more service to unlock {nextDiscount.next}
            </div>
          )}
        </div>

        {/* Category columns — sections sit side by side, cards stack within */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '2rem' }}>
        {SECTIONS.map((sectionName) => {
          const sectionProducts = PRODUCTS.filter(p => p.section === sectionName);
          if (sectionProducts.length === 0) return null;
          // Group products by subcategory (preserving first-seen order).
          const subcats: string[] = [];
          for (const p of sectionProducts) {
            const sc = p.subcategory || '';
            if (!subcats.includes(sc)) subcats.push(sc);
          }
          return (
            <div key={sectionName} style={{
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '1rem',
              backgroundColor: 'rgba(255,255,255,0.02)',
            }}>
              <h3 style={{
                fontSize: '0.95rem', color: '#e2e8f0', textTransform: 'uppercase',
                letterSpacing: '0.05em', margin: '0 0 0.75rem', fontWeight: 700,
              }}>{sectionName}</h3>
              {subcats.map((subcat) => (
                <div key={subcat || 'default'}>
                  {subcat && (
                    <h4 style={{
                      fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase',
                      letterSpacing: '0.04em', margin: '0 0 0.5rem', fontWeight: 600,
                    }}>{subcat}</h4>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {sectionProducts.filter(p => (p.subcategory || '') === subcat).map((product) => {
            const active = isProductActive(product.key);
            const blocked = false;

            return (
              <div
                key={product.key}
                style={{
                  ...cardStyle,
                  ...(active ? { border: `2px solid ${product.color}40`, opacity: 0.85 } : {}),
                  ...(product.key === 'bundle' ? { border: `2px solid ${product.color}`, boxShadow: `0 4px 20px ${product.color}20` } : {}),
                }}
                onMouseEnter={(e) => !active && (e.currentTarget.style.transform = 'translateY(-3px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {/* Name + active marker */}
                <h3 style={{ fontSize: '1rem', margin: '0 0 0.25rem', color: active ? product.color : '#ffffff' }}>
                  {product.name}
                  {active && <span style={{ fontSize: '0.65rem', marginLeft: '0.4rem', color: '#4ade80' }}>ACTIVE</span>}
                </h3>

                {/* Price */}
                <div style={{ marginBottom: '0.15rem' }}>
                  <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: product.color }}>
                    ${billingCycle === 'yearly' ? product.yearlyPrice.toFixed(2) : product.monthlyPrice.toFixed(2)}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{billingCycle === 'yearly' ? '/yr' : '/mo'}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p style={{ color: '#4ade80', fontSize: '0.68rem', margin: '0 0 0.5rem' }}>
                    ${(product.yearlyPrice / 12).toFixed(2)}/mo &mdash; save ${((product.monthlyPrice * 12) - product.yearlyPrice).toFixed(2)}/yr
                  </p>
                )}

                {/* Features — compact, capped at 4 rows to keep cards short */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.6rem', textAlign: 'left', flex: 1 }}>
                  {product.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} style={{ padding: '0.12rem 0', color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.35 }}>
                      <span style={{ color: product.checkColor || '#4ade80', marginRight: '0.3rem' }}>&#10003;</span>{feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {active ? (
                  <button disabled style={{
                    width: '100%', padding: '0.5rem', fontSize: '0.8rem', fontWeight: 'bold',
                    borderRadius: '6px', border: 'none', backgroundColor: '#475569', color: 'white', cursor: 'not-allowed'
                  }}>
                    Active
                  </button>
                ) : blocked ? (
                  <div>
                    <button disabled style={{
                      width: '100%', padding: '0.5rem', fontSize: '0.8rem', fontWeight: 'bold',
                      borderRadius: '6px', border: 'none', backgroundColor: '#475569', color: '#94a3b8', cursor: 'not-allowed'
                    }}>
                      Subscribe to Premier First
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleStripeSubscribe(product)}
                    disabled={pendingProduct !== null}
                    style={{
                      width: '100%', padding: '0.55rem', fontSize: '0.85rem', fontWeight: 'bold',
                      borderRadius: '6px', border: 'none',
                      backgroundColor: pendingProduct === product.key ? '#475569' : product.color,
                      color: 'white',
                      cursor: pendingProduct !== null ? 'wait' : 'pointer',
                      opacity: pendingProduct !== null && pendingProduct !== product.key ? 0.6 : 1,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {pendingProduct === product.key ? 'Redirecting to checkout…' : 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        </div>

        {/* Differentiator Section */}
        <div style={{
          padding: '1.5rem', marginBottom: '2rem',
          backgroundColor: 'rgba(139,92,246,0.1)', borderRadius: '12px',
          border: '1px solid rgba(139,92,246,0.2)',
        }}>
          <h3 style={{ color: '#a78bfa', marginBottom: '0.75rem', fontSize: '1.2rem' }}>
            Why Our Signals Beat ChatGPT
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {[
              { title: 'Live Market Data', desc: 'Real bid/ask, IV, skew from TastyTrade — not cached or guessed' },
              { title: 'Quantitative Models', desc: 'Backtested weighting algorithms, not language model predictions' },
              { title: 'Risk Management', desc: 'Symbol-specific min buffers, credit thresholds, and position sizing' },
              { title: 'Full Transparency', desc: 'Downloadable Word reports with complete rationale for every signal' },
            ].map((item, i) => (
              <div key={i} style={{ padding: '0.75rem' }}>
                <h4 style={{ color: '#c4b5fd', marginBottom: '0.25rem', fontSize: '0.95rem' }}>{item.title}</h4>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#ffffff', textAlign: 'center', fontSize: '1.3rem' }}>
            Frequently Asked Questions
          </h3>

          {faqs.map((faq, idx) => (
            <div key={idx} style={{
              borderBottom: idx < faqs.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              padding: '0.75rem 0'
            }}>
              <button
                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                style={{
                  width: '100%', textAlign: 'left', background: 'none', border: 'none',
                  color: '#ffffff', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                  padding: '0.4rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}
              >
                <span>{faq.q}</span>
                <span style={{ color: '#64748b', fontSize: '1.2rem' }}>{expandedFaq === idx ? '\u2212' : '+'}</span>
              </button>
              {expandedFaq === idx && (
                <p style={{ color: '#94a3b8', marginTop: '0.4rem', lineHeight: '1.6', fontSize: '0.85rem' }}>
                  {faq.a}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center', padding: '1rem' }}>
          <p style={{ color: '#94a3b8', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
            Questions? We're here to help.
          </p>
          <a
            href="mailto:support@wealthfinancialsystems.com"
            style={{
              display: 'inline-block', padding: '0.6rem 1.5rem',
              backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', textDecoration: 'none',
              borderRadius: '8px', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.9rem'
            }}
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};
